import { MediaCrop, MediaKind } from './media-api';

/**
 * The geometry of the frame (the board AdmMediaCrop): a rectangle 375 : 402 in pixels of the auto-oriented original. The rules are those of `ProductMediaRules`
 * of vn (`heightForWidth`, `cropProblem`, `defaultCrop`, `photoWidths`, `videoWidths`, `defaultPosterMs`): what this file draws is what the server accepts.
 */

export const FRAME_W = 375;
export const FRAME_H = 402;
/** The frame is this many pixels wide at least. */
export const FRAME_MIN_W = 375;
/** How far the height of a frame may be from `round(w · 402 / 375)`, pixels. */
export const FRAME_TOLERANCE = 1;
export const WIDTH_LADDER: readonly number[] = [160, 320, 640, 960, 1280, 1600];
export const VIDEO_LADDER: readonly number[] = [720, 1080];
/** The biggest zoom the slider goes to, percent (the frame cannot be narrower than 375 px anyway). */
export const ZOOM_CAP = 400;

export interface Size {
  width: number;
  height: number;
}

export type CropProblem = 'outside' | 'tooSmall' | 'aspect';

/** `round(w · 402 / 375)`: the height of a file of the width (every file has the shape of the frame). */
export function heightForWidth(width: number): number {
  return Math.round((width * FRAME_H) / FRAME_W);
}

/** Why a frame is not usable on an original of `W × H`; `null` = it is. */
export function cropProblem(crop: MediaCrop, size: Size): CropProblem | null {
  if (crop.x < 0 || crop.y < 0 || crop.w <= 0 || crop.h <= 0 || crop.x + crop.w > size.width || crop.y + crop.h > size.height) {
    return 'outside';
  }
  if (crop.w < FRAME_MIN_W) {
    return 'tooSmall';
  }
  if (Math.abs(crop.h - heightForWidth(crop.w)) > FRAME_TOLERANCE) {
    return 'aspect';
  }
  return null;
}

/** The frame the job takes when nobody chose one: the biggest 375 : 402 rectangle that fits into the original, centred. */
export function defaultCrop(size: Size): MediaCrop {
  let w = size.width;
  let h = heightForWidth(w);
  if (h > size.height) {
    w = Math.floor((size.height * FRAME_W) / FRAME_H);
    h = Math.min(size.height, heightForWidth(w));
  }
  return { x: Math.floor((size.width - w) / 2), y: Math.floor((size.height - h) / 2), w, h };
}

/** The widest frame there is (zoom 100 %). */
export function widestFrame(size: Size): number {
  return defaultCrop(size).w;
}

/** The biggest zoom, percent: down to the 375 px of the narrowest frame, at most `ZOOM_CAP`; 100 when the picture is too small to zoom into. */
export function maxZoom(size: Size): number {
  return Math.max(100, Math.min(ZOOM_CAP, Math.floor((widestFrame(size) / FRAME_MIN_W) * 100)));
}

function clamp(value: number, low: number, high: number): number {
  return Math.max(low, Math.min(high, value));
}

/** The frame of this width at this corner, kept inside the original. */
function framed(size: Size, w: number, x: number, y: number): MediaCrop {
  const width = clamp(Math.round(w), 1, size.width);
  const h = Math.min(size.height, heightForWidth(width));
  return { x: clamp(Math.round(x), 0, size.width - width), y: clamp(Math.round(y), 0, size.height - h), w: width, h };
}

/** The zoom of a frame: how many times narrower it is than the widest one, percent. */
export function zoomOf(crop: MediaCrop, size: Size): number {
  return clamp(Math.round((widestFrame(size) / crop.w) * 100), 100, maxZoom(size));
}

/** The same centre, another zoom (percent, 100 = the widest frame). */
export function withZoom(crop: MediaCrop, size: Size, zoom: number): MediaCrop {
  const z = clamp(zoom, 100, maxZoom(size));
  const w = Math.max(FRAME_MIN_W, Math.round((widestFrame(size) * 100) / z));
  const cx = crop.x + crop.w / 2;
  const cy = crop.y + crop.h / 2;
  const h = Math.min(size.height, heightForWidth(Math.min(w, size.width)));
  return framed(size, w, cx - w / 2, cy - h / 2);
}

/** The frame moved by `dx`, `dy` pixels of the original, kept inside it. */
export function movedBy(crop: MediaCrop, size: Size, dx: number, dy: number): MediaCrop {
  return framed(size, crop.w, crop.x + dx, crop.y + dy);
}

/** The place of the frame along an axis, percent of the room it has (0 = at the start, 100 = at the end; 50 when it has no room). */
export function offsetPercent(crop: MediaCrop, size: Size, axis: 'x' | 'y'): number {
  const room = axis === 'x' ? size.width - crop.w : size.height - crop.h;
  if (room <= 0) {
    return 50;
  }
  return clamp(Math.round(((axis === 'x' ? crop.x : crop.y) / room) * 100), 0, 100);
}

/** The frame put at a place along an axis, percent of the room it has. */
export function withOffset(crop: MediaCrop, size: Size, axis: 'x' | 'y', percent: number): MediaCrop {
  const room = axis === 'x' ? size.width - crop.w : size.height - crop.h;
  const at = (clamp(percent, 0, 100) / 100) * Math.max(0, room);
  return axis === 'x' ? framed(size, crop.w, at, crop.y) : framed(size, crop.w, crop.x, at);
}

/** The frame as the server will have it: the height is the width's, inside the original; what is already right stays as it is. */
export function normalizedCrop(crop: MediaCrop, size: Size): MediaCrop {
  return cropProblem(crop, size) === null ? crop : framed(size, crop.w, crop.x, crop.y);
}

/** The widths of the photo files of a frame `cropW` wide: the rungs below it, and the frame's own width up to 1600. No upscaling. */
export function photoWidths(cropW: number): number[] {
  const out = WIDTH_LADDER.filter(w => w < cropW);
  const top = Math.min(cropW, WIDTH_LADDER[WIDTH_LADDER.length - 1]);
  if (!out.includes(top)) {
    out.push(top);
  }
  return out;
}

/** The widths of the video files: 720 / 1080 that fit into the frame; when none does, the frame's own width. */
export function videoWidths(cropW: number): number[] {
  const out = VIDEO_LADDER.filter(w => w <= cropW);
  return out.length > 0 ? out : [cropW];
}

export interface OutputInfo {
  /** `ok`: sharp on every screen; `warn`: the frame is small. */
  level: 'ok' | 'warn';
  widths: number[];
  /** The biggest file. */
  outW: number;
  outH: number;
  cropW: number;
}

/** What the files of a frame will be (the green and the yellow alert of the board). A photo is sharp from 1200 px of frame, a video has its 1080 file from 1080. */
export function outputInfo(kind: MediaKind, cropW: number, goodSide = 1200): OutputInfo {
  const widths = kind === 'VIDEO' ? videoWidths(cropW) : photoWidths(cropW);
  const outW = Math.max(...widths);
  const good = kind === 'VIDEO' ? cropW >= VIDEO_LADDER[VIDEO_LADDER.length - 1] : cropW >= goodSide;
  return { level: good ? 'ok' : 'warn', widths, outW, outH: heightForWidth(outW), cropW };
}

/** The second of the cover that the slider starts at: the default of the server (`min(1000, duration / 2)` ms), in whole seconds, not past the end. */
export function defaultPosterSecond(durationMs: number): number {
  return clamp(Math.round(Math.min(1000, Math.floor(durationMs / 2)) / 1000), 0, lastSecond(durationMs));
}

/** The last whole second the cover can be at. */
export function lastSecond(durationMs: number): number {
  return Math.max(0, Math.floor(durationMs / 1000));
}

// ------------------------------------------------------------------------------------------------------------------------------ pictures in the dialog

export interface Region {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Where to put an `<img>` inside a box so that `region` of the picture fills the box: the percents of the box (`width` of the picture, `left`, `top`; the height follows).
 * `source` is the picture in the units the region is in (the whole original for a preview, the whole sprite for a cell of the strip).
 */
export function layerOf(source: Size, region: Region): { width: string; left: string; top: string } {
  const pct = (value: number): string => `${Math.round(value * 1000) / 1000}%`;
  return {
    width: pct((source.width / region.w) * 100),
    left: pct((-region.x / region.w) * 100),
    top: pct((-region.y / region.h) * 100)
  };
}

/** The same, for the whole picture inside a box that has the picture's own shape: the picture fills the box. */
export const WHOLE_LAYER = { width: '100%', left: '0%', top: '0%' } as const;

// ------------------------------------------------------------------------------------------------------------------------------ the strip of a video

/** The width of a frame of the strip: the job scales them to 240 px (`fps=1,scale=240:-2`). */
export const STRIP_CELL_W = 240;

export interface StripLayout {
  frames: number;
  cellW: number;
  cellH: number;
  vertical: boolean;
}

/**
 * The strip is one picture of the frames the job took, one per second, in a row (`vips arrayjoin`); a column is understood too. `natural` is the size of the picture,
 * `video` the size of the video: a frame is 240 wide and as tall as the video says.
 */
export function stripLayout(natural: Size, video: Size): StripLayout {
  const expectedH = Math.max(2, 2 * Math.round(((STRIP_CELL_W * video.height) / Math.max(1, video.width)) / 2));
  if (natural.width <= STRIP_CELL_W + 1 && natural.height >= expectedH * 1.5) {
    const frames = Math.max(1, Math.round(natural.height / expectedH));
    return { frames, cellW: natural.width, cellH: natural.height / frames, vertical: true };
  }
  const frames = Math.max(1, Math.round(natural.width / STRIP_CELL_W));
  return { frames, cellW: natural.width / frames, cellH: natural.height, vertical: false };
}

/** The corner of the frame of a second in the strip (the second after the last frame is the last frame). */
export function cellCorner(layout: StripLayout, second: number): { x: number; y: number } {
  const i = clamp(Math.round(second), 0, layout.frames - 1);
  return layout.vertical ? { x: 0, y: i * layout.cellH } : { x: i * layout.cellW, y: 0 };
}

/** The seconds the strip of the dialog shows: `count` of them, evenly from the first to the last (all of them when there are fewer). */
export function sampleSeconds(lastSec: number, count = 9): number[] {
  if (lastSec + 1 <= count) {
    return Array.from({ length: lastSec + 1 }, (_, i) => i);
  }
  return Array.from({ length: count }, (_, i) => Math.round((i * lastSec) / (count - 1)));
}

/** Which of the shown seconds is the nearest to the chosen one (to outline it). */
export function nearestShown(shown: readonly number[], second: number): number {
  let best = 0;
  shown.forEach((s, i) => {
    if (Math.abs(s - second) < Math.abs(shown[best] - second)) {
      best = i;
    }
  });
  return best;
}
